import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Skeleton
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { useAuth } from '../../../../auth/useAuth'
import { useTableSort } from '../../../../hooks/useTableSort'
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader'

// Types
interface Account {
  Account_Id: number
  Account_Name: string
  Account_Fund: string
  Account_Currency: string
  Account_Amount_Invested: number | null
  Account_Amount_Commited: number | null  // Note: backend has typo "Commited"
  Account_Asof: Date | null
  Account_Timestamp: Date
  Account_Deleted: boolean
  CreatedBy?: string
  ModifiedBy?: string
}

interface PaginationInfo {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

interface PaginatedResponse {
  data: Account[]
  pagination: PaginationInfo
}

const initialAccount: Omit<Account, 'Account_Id' | 'Account_Timestamp'> = {
  Account_Name: '',
  Account_Fund: '',
  Account_Currency: '',
  Account_Amount_Invested: null,
  Account_Amount_Commited: null,
  Account_Asof: null,
  Account_Deleted: false
}

const AccountsManagement: React.FC = () => {
  const { user, getAccessToken } = useAuth()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)
  const [backgroundLoading, setBackgroundLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Pagination state
  const [totalCount, setTotalCount] = useState(0)
  const [loadedCount, setLoadedCount] = useState(0)
  const [currentBackendPage, setCurrentBackendPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  
  // Form state
  const [formData, setFormData] = useState<Omit<Account, 'Account_Id' | 'Account_Timestamp'>>(initialAccount)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [appliedFilters, setAppliedFilters] = useState<Omit<Account, 'Account_Id' | 'Account_Timestamp'>>(initialAccount)
  
  // Table state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null)

  // Dropdown options
  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD']

  // Load initial accounts data with pagination
  const loadInitialAccounts = async () => {
    setLoading(true)
    setError(null)
    setIsInitialLoad(true)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      // First get total count
      const countResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/accounts/?count_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!countResponse.ok) {
        throw new Error('Failed to get accounts count')
      }
      
      const countData = await countResponse.json()
      setTotalCount(countData.total_count)
      
      // Then load first page (100 records)
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/accounts/?page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to load accounts')
      }
      
      const data: PaginatedResponse = await response.json()
      
      // Convert date strings to Date objects
      const processedAccounts = data.data.map(account => ({
        ...account,
        Account_Asof: account.Account_Asof ? new Date(account.Account_Asof) : null,
        Account_Timestamp: new Date(account.Account_Timestamp)
      }))
      
      setAccounts(processedAccounts)
      setLoadedCount(processedAccounts.length)
      setCurrentBackendPage(1)
      setHasMoreData(data.pagination.has_next)
      
      // Start background loading if there's more data
      if (data.pagination.has_next && countData.total_count > 100) {
        setTimeout(() => loadMoreAccountsInBackground(countData.total_count), 1000)
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts')
      // For development, show sample data
      const sampleAccounts = [
        {
          Account_Id: 1,
          Account_Name: 'Pension Fund A',
          Account_Fund: 'Sample Fund I',
          Account_Currency: 'USD',
          Account_Amount_Invested: 35000000,
          Account_Amount_Commited: 50000000,
          Account_Asof: new Date(),
          Account_Timestamp: new Date(),
          Account_Deleted: false,
          CreatedBy: user?.username || user?.homeAccountId || 'system',
          ModifiedBy: user?.username || user?.homeAccountId || 'system'
        },
        {
          Account_Id: 2,
          Account_Name: 'Endowment Fund B',
          Account_Fund: 'Sample Fund II',
          Account_Currency: 'EUR',
          Account_Amount_Invested: 20000000,
          Account_Amount_Commited: 25000000,
          Account_Asof: new Date(),
          Account_Timestamp: new Date(),
          Account_Deleted: false,
          CreatedBy: user?.username || user?.homeAccountId || 'system',
          ModifiedBy: user?.username || user?.homeAccountId || 'system'
        }
      ]
      setAccounts(sampleAccounts)
      setTotalCount(2)
      setLoadedCount(2)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }

  // Load all remaining accounts in background
  const loadMoreAccountsInBackground = async (totalRecords: number) => {
    if (!hasMoreData || backgroundLoading) return
    
    setBackgroundLoading(true)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        return
      }
      
      // Calculate how many records remain
      const remainingRecords = totalRecords - loadedCount
      if (remainingRecords <= 0) {
        setHasMoreData(false)
        return
      }
      
      // Load all remaining records in one request using page=1 with a large limit
      // This ensures we get all records from the beginning, then we'll replace the dataset
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/accounts/?page=1&limit=${totalRecords}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        return
      }
      
      const data: PaginatedResponse = await response.json()
      
      // Convert date strings to Date objects
      const processedAccounts = data.data.map(account => ({
        ...account,
        Account_Asof: account.Account_Asof ? new Date(account.Account_Asof) : null,
        Account_Timestamp: new Date(account.Account_Timestamp)
      }))
      
      // Replace all accounts with the complete dataset
      setAccounts(processedAccounts)
      setLoadedCount(processedAccounts.length)
      setHasMoreData(false) // All data loaded
      
    } catch (err) {
      // Silently fail background loading
      console.warn('Background loading failed:', err)
    } finally {
      setBackgroundLoading(false)
    }
  }

  // Refresh all data
  const refreshAccounts = () => {
    setAccounts([])
    setTotalCount(0)
    setLoadedCount(0)
    setCurrentBackendPage(1)
    setHasMoreData(true)
    loadInitialAccounts()
  }

  useEffect(() => {
    loadInitialAccounts()
  }, [])

  // Handle form changes
  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setSuccess(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const url = editingId 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/accounts/${editingId}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/accounts/`
      
      const method = editingId ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          CreatedBy: editingId ? undefined : user?.username || user?.homeAccountId || 'system',
          ModifiedBy: user?.username || user?.homeAccountId || 'system'
        })
      })
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingId ? 'update' : 'create'} account`)
      }
      
      const result = await response.json()
      const updatedAccount = result.account
      
      if (editingId) {
        // Update existing account in the list
        setAccounts(prevAccounts => prevAccounts.map(account => 
          account.Account_Id === editingId ? {
            ...updatedAccount,
            Account_Asof: updatedAccount.Account_Asof ? new Date(updatedAccount.Account_Asof) : null,
            Account_Timestamp: new Date(updatedAccount.Account_Timestamp)
          } : account
        ))
      } else {
        // Add new account to the list
        const newAccount = {
          ...updatedAccount,
          Account_Asof: updatedAccount.Account_Asof ? new Date(updatedAccount.Account_Asof) : null,
          Account_Timestamp: new Date(updatedAccount.Account_Timestamp)
        }
        setAccounts(prevAccounts => [newAccount, ...prevAccounts])
        setTotalCount(prevCount => prevCount + 1)
        setLoadedCount(prevCount => prevCount + 1)
      }
      
      setSuccess(`Account ${editingId ? 'updated' : 'created'} successfully`)
      handleClear()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle edit
  const handleEdit = (account: Account) => {
    try {
      // Convert date strings to Date objects if they're strings
      const parseDate = (dateValue: any): Date | null => {
        if (!dateValue) return null
        if (dateValue instanceof Date) return dateValue
        if (typeof dateValue === 'string') {
          const parsed = new Date(dateValue)
          return isNaN(parsed.getTime()) ? null : parsed
        }
        return null
      }

      setFormData({
        Account_Name: account.Account_Name || '',
        Account_Fund: account.Account_Fund || '',
        Account_Currency: account.Account_Currency || '',
        Account_Amount_Invested: account.Account_Amount_Invested,
        Account_Amount_Commited: account.Account_Amount_Commited,
        Account_Asof: parseDate(account.Account_Asof),
        Account_Deleted: account.Account_Deleted || false
      })
      setEditingId(account.Account_Id)
      
      // Scroll to the form section to make it visible
      const formElement = document.querySelector('[data-form-section]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      console.error('Error in handleEdit:', error)
      setError('Failed to load account data for editing')
    }
  }

  // Handle copy as new (create new entry with account data)
  const handleCopyAsNew = (account: Account) => {
    try {
      // Convert date strings to Date objects if they're strings
      const parseDate = (dateValue: any): Date | null => {
        if (!dateValue) return null
        if (dateValue instanceof Date) return dateValue
        if (typeof dateValue === 'string') {
          const parsed = new Date(dateValue)
          return isNaN(parsed.getTime()) ? null : parsed
        }
        return null
      }

      setFormData({
        Account_Name: account.Account_Name || '',
        Account_Fund: account.Account_Fund || '',
        Account_Currency: account.Account_Currency || '',
        Account_Amount_Invested: account.Account_Amount_Invested,
        Account_Amount_Commited: account.Account_Amount_Commited,
        Account_Asof: new Date(), // Set to today for new entry
        Account_Deleted: false
      })
      setEditingId(null) // Clear editing ID to indicate this is a new entry
      
      setSuccess(`Form pre-filled with data from "${account.Account_Name}". Update As Of date and amounts, then click Submit to create new entry.`)
      
      // Scroll to the form section to make it visible
      const formElement = document.querySelector('[data-form-section]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      console.error('Error in handleCopyAsNew:', error)
      setError('Failed to load account data for copying')
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (!accountToDelete) return
    
    setLoading(true)
    setError(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/accounts/${accountToDelete.Account_Id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete account')
      }
      
      setSuccess('Account deleted successfully')
      setDeleteDialogOpen(false)
      setAccountToDelete(null)
      refreshAccounts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle clear form
  const handleClear = () => {
    setFormData(initialAccount)
    setAppliedFilters(initialAccount)
    setEditingId(null)
  }

  // Handle search button click
  const handleSearch = () => {
    setAppliedFilters({ ...formData })
    setPage(0) // Reset pagination when filtering
  }

  // Check if any filter criteria are set in form
  const hasFilterCriteria = formData.Account_Name || formData.Account_Fund || formData.Account_Currency || 
    formData.Account_Amount_Invested !== null || formData.Account_Amount_Commited !== null || formData.Account_Asof

  // Check if any filters are currently applied
  const hasAppliedFilters = appliedFilters.Account_Name || appliedFilters.Account_Fund || appliedFilters.Account_Currency || 
    appliedFilters.Account_Amount_Invested !== null || appliedFilters.Account_Amount_Commited !== null || appliedFilters.Account_Asof

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedFilters || (
      (!appliedFilters.Account_Name || account.Account_Name.toLowerCase().includes(appliedFilters.Account_Name.toLowerCase())) &&
      (!appliedFilters.Account_Fund || account.Account_Fund.toLowerCase().includes(appliedFilters.Account_Fund.toLowerCase())) &&
      (!appliedFilters.Account_Currency || account.Account_Currency === appliedFilters.Account_Currency) &&
      (appliedFilters.Account_Amount_Invested === null || account.Account_Amount_Invested === appliedFilters.Account_Amount_Invested) &&
      (appliedFilters.Account_Amount_Commited === null || account.Account_Amount_Commited === appliedFilters.Account_Amount_Commited) &&
      (!appliedFilters.Account_Asof || (account.Account_Asof && account.Account_Asof.toDateString() === appliedFilters.Account_Asof.toDateString()))
    )
    
    return matchesFormFilters
  })

  // Apply sorting to filtered accounts
  const { sortedData: sortedAccounts, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredAccounts,
    initialSort: [{ field: 'Account_Name', direction: 'asc' }]
  })

  // Paginated accounts
  const paginatedAccounts = sortedAccounts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
          Accounts Management
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }} data-form-section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">
                  {editingId ? 'Edit Account' : hasFilterCriteria ? 'Add New Account / Search Filters' : 'Add New Account'}
                </Typography>
              </Box>
              {editingId && (
                <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    You are updating an existing account. To create a new entry instead, use the "Copy as New" button from the table.
                  </Typography>
                </Alert>
              )}
              {hasFilterCriteria && !editingId && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    Form fields are being used to filter the accounts list. Click "Search" to apply filters or "Clear" to reset.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Account Name *"
                    value={formData.Account_Name}
                    onChange={(e) => handleInputChange('Account_Name', e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Account Fund"
                    value={formData.Account_Fund}
                    onChange={(e) => handleInputChange('Account_Fund', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.Account_Currency}
                      onChange={(e) => handleInputChange('Account_Currency', e.target.value)}
                      label="Currency"
                    >
                      {currencies.map(currency => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Amount Committed"
                    type="number"
                    value={formData.Account_Amount_Commited !== null && formData.Account_Amount_Commited !== undefined ? formData.Account_Amount_Commited : ''}
                    onChange={(e) => handleInputChange('Account_Amount_Commited', e.target.value !== '' ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Amount Invested"
                    type="number"
                    value={formData.Account_Amount_Invested !== null && formData.Account_Amount_Invested !== undefined ? formData.Account_Amount_Invested : ''}
                    onChange={(e) => handleInputChange('Account_Amount_Invested', e.target.value !== '' ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="As Of Date"
                    value={formData.Account_Asof}
                    onChange={(date) => handleInputChange('Account_Asof', date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.Account_Deleted}
                        onChange={(e) => handleInputChange('Account_Deleted', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Deleted"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading || !formData.Account_Name}
                    sx={{ flex: hasFilterCriteria && !editingId ? 1 : 2 }}
                  >
                    {loading ? 'Processing...' : (editingId ? 'Update' : 'Submit')}
                  </Button>
                  {hasFilterCriteria && !editingId && (
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      Search
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Table Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6">
                    Accounts List ({filteredAccounts.length} of {totalCount})
                  </Typography>
                  {hasAppliedFilters && (
                    <Typography variant="body2" color="text.secondary">
                      Filtered by form criteria
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton onClick={refreshAccounts} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableHeader
                        field="Account_Name"
                        label="Account Name"
                        sortDirection={getSortDirection('Account_Name')}
                        sortIndex={getSortIndex('Account_Name')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Account_Fund"
                        label="Fund"
                        sortDirection={getSortDirection('Account_Fund')}
                        sortIndex={getSortIndex('Account_Fund')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Account_Currency"
                        label="Currency"
                        sortDirection={getSortDirection('Account_Currency')}
                        sortIndex={getSortIndex('Account_Currency')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Account_Amount_Commited"
                        label="Committed"
                        sortDirection={getSortDirection('Account_Amount_Commited')}
                        sortIndex={getSortIndex('Account_Amount_Commited')}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader
                        field="Account_Amount_Invested"
                        label="Invested"
                        sortDirection={getSortDirection('Account_Amount_Invested')}
                        sortIndex={getSortIndex('Account_Amount_Invested')}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader
                        field="Account_Asof"
                        label="As Of"
                        sortDirection={getSortDirection('Account_Asof')}
                        sortIndex={getSortIndex('Account_Asof')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Account_Timestamp"
                        label="Timestamp"
                        sortDirection={getSortDirection('Account_Timestamp')}
                        sortIndex={getSortIndex('Account_Timestamp')}
                        onSort={handleSort}
                      />
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && accounts.length === 0 ? (
                      // Show skeleton rows while loading
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Skeleton variant="circular" width={24} height={24} />
                              <Skeleton variant="circular" width={24} height={24} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : paginatedAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No accounts found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedAccounts.map((account) => (
                        <TableRow key={account.Account_Id}>
                          <TableCell>{account.Account_Name}</TableCell>
                          <TableCell>{account.Account_Fund}</TableCell>
                          <TableCell>{account.Account_Currency}</TableCell>
                          <TableCell align="right">
                            {account.Account_Amount_Commited !== null && account.Account_Amount_Commited !== undefined ? account.Account_Amount_Commited.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {account.Account_Amount_Invested !== null && account.Account_Amount_Invested !== undefined ? account.Account_Amount_Invested.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell>
                            {account.Account_Asof ? account.Account_Asof.toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {account.Account_Timestamp ? new Date(account.Account_Timestamp).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Copy as New Entry">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyAsNew(account)}
                                  disabled={loading}
                                  color="primary"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit This Entry">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(account)}
                                  disabled={loading}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setAccountToDelete(account)
                                    setDeleteDialogOpen(true)
                                  }}
                                  disabled={loading}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredAccounts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10))
                  setPage(0)
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the account "{accountToDelete?.Account_Name}"?
              This action will mark the account as deleted but preserve the data for audit purposes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" disabled={loading}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default AccountsManagement